/*
 * <store-contact-cta> — VIS-103.
 *
 * Hydrates the PDP "Contatta il tuo centro ottico" CTA from the visitor's
 * favorite store (localStorage `vo:favoriteStore`, owned by store-favorites.js):
 *
 *  - With no favorite set, the button opens the store-selector popup instead of
 *    navigating (VIS-805): it becomes a [data-store-selector-trigger], handled
 *    by store-selector.js's delegated listener. The server-rendered locator
 *    href stays in place as the no-JS / not-yet-hydrated fallback.
 *  - With a favorite set the trigger attribute comes off and the href swaps to
 *    that store's detail page, resolved by favoriteStoreUrl() so favorites
 *    saved without a page URL still get one derived from the handle
 *    (VIS-774 / VIS-851).
 *  - The info row ([data-contact-store-info]) stays hidden until a favorite
 *    exists. The store name inside it is filled by the shared store-selector
 *    `[data-store-favorite-name]` reflection, so we only own visibility + href.
 *
 * Re-runs on `store-favorite:changed` so selecting/clearing the favorite (e.g.
 * via the inline "Modifica" link) updates the CTA in place.
 */

import { getFavorite, favoriteStoreUrl } from '@theme/store-favorites';

class StoreContactCta extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('[data-contact-store-link]');
    this.info = this.querySelector('[data-contact-store-info]');
    this.locatorUrl = this.dataset.locatorUrl || '/pages/trova-negozio';

    window.addEventListener('store-favorite:changed', this.#reflect);
    this.#reflect();
  }

  disconnectedCallback() {
    window.removeEventListener('store-favorite:changed', this.#reflect);
  }

  #reflect = () => {
    const favorite = getFavorite();
    const hasFavorite = favorite != null;

    if (this.link) {
      this.link.href = favoriteStoreUrl(favorite) || this.locatorUrl;
      if (hasFavorite) {
        this.link.removeAttribute('data-store-selector-trigger');
        this.link.removeAttribute('data-store-nudge-skip');
      } else {
        // Open the store-selector popup instead of navigating (VIS-805). The
        // nudge-skip keeps the VIS-565 coachmark anchored to the header/menu
        // controls rather than this mid-page button.
        this.link.setAttribute('data-store-selector-trigger', '');
        this.link.setAttribute('data-store-nudge-skip', '');
      }
    }
    if (this.info) this.info.hidden = !hasFavorite;
  };
}

if (!customElements.get('store-contact-cta')) {
  customElements.define('store-contact-cta', StoreContactCta);
}
